import { useState } from 'react';
import {Button, Card, Col, Form, Row, Spinner, Alert, Table} from 'react-bootstrap';
import { PageHeader } from '../components/PageHeader';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchContactSearch } from '../api/contactApi';
import { formatDate } from '../utils/formatters';

function SearchResultCard({ title, isLoading, children }) {
  return (
    <Card className="shadow-sm border-0 mb-3">
      <Card.Header>{title}</Card.Header>
      <Card.Body className="p-0">
        {isLoading ? (
          <div className="p-3 text-center"><Spinner animation="border" /></div>
        ) : (
          children
        )}
      </Card.Body>
    </Card>
  );
}

export function ContactSearchPage() {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['contact-search', searchTerm],
    queryFn: fetchContactSearch,
    refetchOnWindowFocus: false,
    enabled: !!searchTerm,
    retry: (failureCount, error) => {
      if (error.status === 400) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const handleSearch = (e) => {
    e.preventDefault();
    queryClient.resetQueries({ queryKey: ['contact-search', query] });
    setSearchTerm(query);
  };

  const errorMessage = isError ? (error.response?.error || error.response?.message || error.message) : '';

  return (
    <div className="page-container">
      <PageHeader title="Contact search" />
      <Row className="g-3">
        <Col lg={4}>
          <Card className="shadow-sm border-0">
            <Card.Header>Search</Card.Header>
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Form.Group>
                  <Form.Label>Search for contacts</Form.Label>
                  <Form.Control 
                    value={query} 
                    onChange={(event) => setQuery(event.target.value)}
                    disabled={isFetching}
                  />
                </Form.Group>
                <Button 
                  className="mt-3" 
                  variant="success" 
                  type="submit"
                  disabled={isFetching}
                >
                  {isFetching ? <Spinner animation="border" size="sm" /> : 'Search'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8}>
          {errorMessage && (
            <Alert variant="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}
          {data?.info && (
              <Alert variant="info" className="mb-3">
                {data.info}
              </Alert>
          )}

          {searchTerm && !isFetching && !isError && 
           data?.partners.length === 0 && 
           data?.leads.length === 0 && 
           Object.keys(data?.partners_summary || {}).length === 0 && 
           Object.keys(data?.leads_summary || {}).length === 0 && (
            <Alert variant="warning" className="mb-3">
              No results found for "{searchTerm}"
            </Alert>
          )}
          
          <SearchResultCard title="Partners" isLoading={isFetching}>
            {data?.partners.length > 0 && (
              <Table striped className="mb-0">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Date created</th>
                    <th>Orders</th>
                    <th>Last order</th>
                    <th>Can rotate</th>
                    <th>Manager</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.partners.map((partner, idx) => (
                    <tr key={idx} className={partner.can_rotate ? 'table-success' : 'table-danger'}>
                      <td>{partner.name}</td>
                      <td>{formatDate(partner.create_date, 'P')}</td>
                      <td>{partner.num_orders}</td>
                      <td>{formatDate(partner.last_order_date, 'P')}</td>
                      <td>{partner.can_rotate ? 'Yes' : 'No'}</td>
                      <td>{partner.user_id}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {data?.partners.length === 0 && data?.partners_summary && Object.keys(data.partners_summary).length > 0 && (
              <Table striped className="mb-0">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>Number of results</th>
                  </tr>
                </thead>
                <tbody>
                {Object.entries(data.partners_summary).map(([name, count]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td>{count}</td>
                  </tr>
                ))}
                </tbody>
              </Table>
            )}
          </SearchResultCard>

          <SearchResultCard title="CRM Leads" isLoading={isFetching}>
            {data?.leads.length > 0 && (
              <Table striped className="mb-0">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Date created</th>
                    <th>Can rotate</th>
                    <th>Manager</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.leads.map(lead => (
                    <tr key={lead.mid} className={lead.can_rotate ? 'table-success' : 'table-danger'}>
                      <td>{lead.name}</td>
                      <td>{formatDate(lead.create_date, 'P')}</td>
                      <td>{lead.can_rotate ? 'Yes' : 'No'}</td>
                      <td>{lead.user_id}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {data?.leads.length === 0 && data?.leads_summary && Object.keys(data.leads_summary).length > 0 && (
              <Table striped className="mb-0">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>Number of results</th>
                  </tr>
                </thead>
                <tbody>
                {Object.entries(data.leads_summary).map(([name, count]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td>{count}</td>
                  </tr>
                ))}
                </tbody>
              </Table>
            )}
          </SearchResultCard>
        </Col>
      </Row>
    </div>
  );
}
